import { useEffect } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import './GridContainer.css';
import type { Panel as PanelType, DashboardLayout } from '@/types/dashboard';
import { Panel } from './Panel';

const ResponsiveGridLayout = WidthProvider(Responsive);

interface GridContainerProps {
  layout: DashboardLayout;
  panels: PanelType[];
  onLayoutChange: (layout: Array<{ i: string; x: number; y: number; w: number; h: number }>) => void;
  onPanelVisible: (panelId: string) => void;
  onEditPanel?: (panelId: string) => void;
  onDeletePanel?: (panelId: string) => void;
  onDuplicatePanel?: (panelId: string) => void;
  isEditable?: boolean;
  bordered?: boolean;
  compactType?: 'vertical' | 'horizontal' | null;
  noNegativeMargins?: boolean;
}

export function GridContainer({
  layout,
  panels,
  onLayoutChange,
  onPanelVisible,
  onEditPanel,
  onDeletePanel,
  onDuplicatePanel,
  isEditable = false,
  bordered = true,
  compactType = 'vertical',
  noNegativeMargins = false,
}: GridContainerProps) {
  const gridLayout = panels.map(panel => ({
    i: panel.id,
    x: panel.x,
    y: panel.y,
    w: panel.w,
    h: panel.h,
  }));

  useEffect(() => {
    const unloadedPanels = panels.filter(p => p._dataLoaded !== true && p._isLoading !== true);
    if (unloadedPanels.length === 0) return;

    unloadedPanels.forEach(panel => {
      onPanelVisible(panel.id);
    });
  }, [panels, onPanelVisible]);

  const handleDragStop = (layout: Array<{ i: string; x: number; y: number; w: number; h: number }>) => {
    onLayoutChange(layout);
  };

  const handleResizeStop = (layout: Array<{ i: string; x: number; y: number; w: number; h: number }>) => {
    onLayoutChange(layout);
  };

  if (!bordered) {
    const gapClass = `list-spacing-${layout.listSpacing ?? 2}`;
    return (
      <div className={`dashboard-list-view ${gapClass}`}>
        {panels.map(panel => (
          <div key={panel.id} className="dashboard-list-item">
            <Panel
              panel={panel}
              onEdit={onEditPanel}
              onDelete={onDeletePanel}
              onDuplicate={onDuplicatePanel}
              isEditable={isEditable}
              bordered={false}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <ResponsiveGridLayout
      className="layout"
      layouts={{ lg: gridLayout }}
      breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
      cols={{ lg: 12, md: 12, sm: 6, xs: 4 }}
      rowHeight={layout.cellHeight}
      margin={Array.isArray(layout.margin) ? layout.margin : [layout.margin, layout.margin]}
      style={noNegativeMargins ? undefined : {
        marginLeft: -(Array.isArray(layout.margin) ? layout.margin[0] : layout.margin),
        marginRight: -(Array.isArray(layout.margin) ? layout.margin[0] : layout.margin),
      }}
      onDragStop={handleDragStop}
      onResizeStop={handleResizeStop}
      isDraggable={isEditable}
      isResizable={isEditable}
      resizeHandles={['se', 'sw']}
      draggableCancel=".panel-actions, a, .btn, button, input, select, textarea"
      useCSSTransforms={true}
      compactType={compactType}
    >
      {panels.map(panel => (
        <div key={panel.id} data-grid={{x: panel.x, y: panel.y, w: panel.w, h: panel.h, static: !isEditable}}>
          <Panel
            panel={panel}
            onEdit={onEditPanel}
            onDelete={onDeletePanel}
            onDuplicate={onDuplicatePanel}
            isEditable={isEditable}
            bordered={bordered}
          />
        </div>
      ))}
    </ResponsiveGridLayout>
  );
}

export default GridContainer;

